import React, { useState, useRef, useEffect } from "react"
import cn from "../../../utils/classes"

interface InlinePopoverProps {
  triggerElement: React.ReactNode
  children: React.ReactNode
  position?: "right" | "left" | "top" | "bottom"
  offset?: number
  styleOverrides?: string
  containerStyleOverrides?: string
}

/**
 * A simple inline popover that renders within the DOM hierarchy (no portal).
 * Perfect for use within scrollable containers where you want the popover to
 * respect the parent's stacking context and hide behind sticky headers.
 */
export const InlinePopover: React.FC<InlinePopoverProps> = ({
  triggerElement,
  children,
  position = "right",
  offset = 15,
  styleOverrides,
  containerStyleOverrides,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    setIsOpen((prev) => !prev)
  }

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  // Position styles based on position prop
  const getPositionStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: "absolute",
      zIndex: 15,
    }

    switch (position) {
      case "right":
        return {
          ...baseStyles,
          left: `calc(100% + ${offset}px)`,
          top: "50%",
          transform: "translateY(-50%)",
        }
      case "left":
        return {
          ...baseStyles,
          right: `calc(100% + ${offset}px)`,
          top: "50%",
          transform: "translateY(-50%)",
        }
      case "top":
        return {
          ...baseStyles,
          bottom: `calc(100% + ${offset}px)`,
          left: "50%",
          transform: "translateX(-50%)",
        }
      case "bottom":
        return {
          ...baseStyles,
          top: `calc(100% + ${offset}px)`,
          left: "50%",
          transform: "translateX(-50%)",
        }
      default:
        return baseStyles
    }
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative inline-block overflow-visible",
        containerStyleOverrides,
      )}
    >
      <div
        className={cn("cursor-pointer", styleOverrides)}
        role="button"
        aria-expanded={isOpen}
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            handleToggle()
          }
        }}
      >
        {triggerElement}
      </div>
      {isOpen && (
        <div
          ref={popoverRef}
          style={{
            ...getPositionStyles(),
            width: "max-content",
            maxWidth: "190px",
          }}
          className="whitespace-normal"
        >
          {children}
        </div>
      )}
    </div>
  )
}
